import { type ResourceData, registerBundle } from "@tillix/i18n";

type Join<K, P> = K extends string
  ? P extends string
    ? `${K}.${P}`
    : never
  : never;

/**
 * Dotted key paths of `T` whose leaf value extends `Leaf`. Arrays are leaves
 * (never descended into), so `LeafPaths<R, readonly string[]>` yields the
 * paragraph-list keys and `LeafPaths<R, string>` the plain-string keys.
 */
export type LeafPaths<T, Leaf> = {
  [K in keyof T & string]: T[K] extends Leaf
    ? K
    : T[K] extends readonly unknown[]
      ? never
      : T[K] extends object
        ? Join<K, LeafPaths<T[K], Leaf>>
        : never;
}[keyof T & string];

export type StringKey<R> = LeafPaths<R, string>;
export type ListKey<R> = LeafPaths<R, readonly string[]>;

/** The shape both `useT().t` and `getTranslations().t` expose. */
export type RawT = (key: string, options?: Record<string, unknown>) => string;

export interface FlowT<R> {
  (key: StringKey<R>, vars?: Record<string, unknown>): string;
  /** A JSON array value (paragraph list), interpolated per entry. */
  list(key: ListKey<R>): string[];
}

export interface FlowNamespace<R extends ResourceData> {
  ns: string;
  /** `registerBundle` for the en resources; call once at module scope. */
  register(): void;
  /** Type the raw runtime `t` against this namespace's en shape. */
  wrap(raw: RawT): FlowT<R>;
}

/**
 * A flux-owned i18n namespace: one `en.json`, registered into the
 * `@tillix/i18n` registry, with a typed `t` whose keys are checked against
 * the JSON shape. `defaults` are interpolation variables merged into every
 * call (e.g. a tenant's support phone number), so call sites don't repeat
 * them.
 */
export function defineNamespace<R extends ResourceData>(options: {
  ns: string;
  en: R;
  /** Spanish resources with the same shape as `en`; registered alongside it. */
  es?: R;
  defaults?: Record<string, unknown>;
}): FlowNamespace<R> {
  const { ns, en, es, defaults = {} } = options;
  return {
    ns,
    register() {
      registerBundle({ ns, resources: es ? { en, es } : { en } });
    },
    wrap(raw) {
      const callable = (key: string, vars?: Record<string, unknown>) =>
        raw(key, { ...defaults, ...vars });
      const list = (key: string) => {
        const value = raw(key, { ...defaults, returnObjects: true }) as unknown;
        return Array.isArray(value)
          ? value.map((entry) => String(entry))
          : [String(value)];
      };
      return Object.assign(callable, { list }) as FlowT<R>;
    },
  };
}
